import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import type { Brand } from "../api/types";
import { titleCase } from "../lib/format";

// ----- Status pills ---------------------------------------------------------
// Muted dark tints that remain legible on the luxe portal chrome.

const STATUS_TONES: Record<string, string> = {
  // Event lifecycle
  published: "bg-sky-400/12 text-sky-100 ring-sky-300/28",
  live: "bg-emerald-400/12 text-emerald-100 ring-emerald-300/28",
  active: "bg-emerald-400/12 text-emerald-100 ring-emerald-300/28",
  completed: "bg-white/8 text-ink-soft ring-white/14",
  archived: "bg-white/8 text-ink-soft ring-white/14",
  cancelled: "bg-rose-400/12 text-rose-100 ring-rose-300/28",
  canceled: "bg-rose-400/12 text-rose-100 ring-rose-300/28",
  // Guest lifecycle
  confirmed: "bg-emerald-400/12 text-emerald-100 ring-emerald-300/28",
  declined: "bg-rose-400/12 text-rose-100 ring-rose-300/28",
  pending: "bg-amber-300/12 text-amber-100 ring-amber-200/28",
  invited: "bg-sky-400/12 text-sky-100 ring-sky-300/28",
  sent: "bg-sky-400/12 text-sky-100 ring-sky-300/28",
  waitlisted: "bg-violet-400/12 text-violet-100 ring-violet-300/28",
  draft: "bg-white/8 text-ink-soft ring-white/14",
};

export function StatusPill({ status }: { status: string | null | undefined }) {
  if (!status) return null;
  const tone =
    STATUS_TONES[status.toLowerCase()] ?? "bg-white/8 text-ink-soft ring-white/14";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-[0.08em] ring-1 ring-inset ${tone}`}
    >
      {titleCase(status)}
    </span>
  );
}

// ----- Attend wordmark --------------------------------------------------------
// The real ATTEND logotype — same asset the admin auth shell uses. The
// .attend-wordmark utility (index.css) keeps the baked dark-navy mark on
// light chrome and inverts it to white on dark, following the theme cascade.

const ATTEND_WORDMARK_URL = "https://files.attend.vip/logo/logo_extra-small.svg";

export function AttendWordmark({ className = "h-4" }: { className?: string }) {
  return (
    <img
      src={ATTEND_WORDMARK_URL}
      alt="Attend"
      className={`attend-wordmark w-auto ${className}`}
    />
  );
}

type ThemeMode = "light" | "dark" | "system";

const THEME_KEY = "attendvip:client-portal:theme";
const THEME_OPTIONS: ThemeMode[] = ["light", "dark", "system"];

function readTheme(): ThemeMode {
  if (typeof window === "undefined") return "system";
  const saved = window.localStorage.getItem(THEME_KEY);
  return saved === "light" || saved === "dark" || saved === "system" ? saved : "system";
}

function applyTheme(mode: ThemeMode) {
  const root = document.documentElement;
  if (mode === "system") root.removeAttribute("data-theme");
  else root.dataset.theme = mode;
}

export function PortalFooter({ className = "" }: { className?: string }) {
  const [theme, setTheme] = useState<ThemeMode>(() => readTheme());

  useEffect(() => {
    applyTheme(theme);
    window.localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  return (
    <div
      className={`flex flex-col items-center justify-between gap-3 text-center text-[11px] text-ink-faint sm:flex-row ${className}`}
    >
      {/* ≤768px the legal links drop to their own row under the copyright;
          wider viewports keep the single dot-separated line. */}
      <div className="flex flex-col items-center justify-center gap-x-2 gap-y-1 min-[769px]:flex-row">
        <span>© {new Date().getFullYear()} Kardoe Incorporated</span>
        <span aria-hidden className="hidden min-[769px]:inline">
          ·
        </span>
        <span className="flex items-center gap-2">
          <a
            href="https://www.attend.vip/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2 transition hover:text-ink"
          >
            Terms
          </a>
          <span aria-hidden>·</span>
          <a
            href="https://www.attend.vip/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2 transition hover:text-ink"
          >
            Privacy Policy
          </a>
        </span>
      </div>
      <div className="flex rounded-full border border-hairline bg-card/60 p-0.5">
        {THEME_OPTIONS.map((option) => (
          <button
            key={option}
            type="button"
            aria-pressed={theme === option}
            className={`rounded-full px-2.5 py-1 text-[11px] font-medium capitalize transition ${
              theme === option ? "bg-ink text-paper" : "text-ink-faint hover:text-ink"
            }`}
            onClick={() => setTheme(option)}
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );
}

// ----- Brand mark -----------------------------------------------------------
// Small by design: restrained brand reads as classy, oversized reads as
// shouty. Falls back to a monogram tile tinted with the accent.

export function BrandMark({
  brand,
  size = "sm",
}: {
  brand: Brand | null | undefined;
  size?: "sm" | "md";
}) {
  if (!brand) return null;
  const box = size === "md" ? "h-8 w-8 text-[13px]" : "h-5 w-5 text-[10px]";
  const img = size === "md" ? "h-8" : "h-5";
  if (brand.logoUrl) {
    return (
      <img
        src={brand.logoUrl}
        alt={brand.name ?? "Brand"}
        className={`${img} w-auto max-w-[120px] object-contain`}
      />
    );
  }
  const initial = (brand.name ?? "?").trim().charAt(0).toUpperCase() || "?";
  const fallback = brand.accentColor == null;
  return (
    <span
      className={`inline-flex ${box} items-center justify-center rounded-md border border-white/20 font-semibold text-white shadow-card`}
      style={{
        backgroundColor: brand.accentColor ?? "#D5B56F",
        color: fallback ? "#070911" : "#ffffff",
      }}
      aria-hidden
    >
      {initial}
    </span>
  );
}

// ----- States ----------------------------------------------------------------

export function LoadingState({ label = "Loading…" }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-20 text-ink-soft">
      <Loader2 size={16} className="animate-spin" />
      <span className="text-[14px]">{label}</span>
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="luxe-panel mx-auto max-w-md rounded-card px-6 py-12 text-center">
      <div className="text-[15px] font-medium text-ink">Something went wrong</div>
      <div className="mt-1 text-[13.5px] text-ink-soft">{message}</div>
      {onRetry && (
        <button type="button" onClick={onRetry} className="btn-quiet mt-4">
          Try again
        </button>
      )}
    </div>
  );
}

export function EmptyState({ title, body }: { title: string; body?: string }) {
  return (
    <div className="mx-auto max-w-md rounded-card border border-dashed border-hairline bg-card/60 py-14 text-center shadow-card">
      <div className="text-[15px] font-medium text-ink">{title}</div>
      {body && <div className="mt-1 px-6 text-[13.5px] text-ink-soft">{body}</div>}
    </div>
  );
}

// ----- Report stat tile -------------------------------------------------------

export function StatTile({
  label,
  value,
  accent,
}: {
  label: string;
  value: number | string;
  accent?: string | null;
}) {
  return (
    <div className="luxe-panel rounded-card px-5 py-4">
      <div className="eyebrow">{label}</div>
      <div
        className="mt-1.5 font-serif text-[30px] leading-none tracking-tight text-champagne"
        style={accent ? { color: accent } : undefined}
      >
        {value}
      </div>
    </div>
  );
}
